import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { firestore, auth } from '../lib/firebase';
import type { MediaAsset, MediaAssetPayload } from '../types/media';

const mediaCollection = collection(firestore, 'mediaAssets');

const apiBaseUrl = (() => {
  const base = import.meta.env.VITE_MEDIA_API_BASE_URL ?? '/api';
  return base.endsWith('/') ? base.slice(0, -1) : base;
})();

type UploadParams = {
  placement: string;
  file?: File;
  thumbnailFile?: File;
  removeMediaPath?: string | null;
  removeThumbnailPath?: string | null;
};

type UploadResponse = {
  mediaUrl?: string;
  thumbnailUrl?: string;
  mediaPath?: string;
  thumbnailPath?: string;
};

const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sessione scaduta. Effettua di nuovo il login.');
  }
  return user.getIdToken();
};

const uploadMediaFiles = async ({
  placement,
  file,
  thumbnailFile,
  removeMediaPath,
  removeThumbnailPath,
}: UploadParams): Promise<UploadResponse> => {
  const shouldUpload = Boolean(file || thumbnailFile);
  const shouldRemove = Boolean(removeMediaPath || removeThumbnailPath);

  if (!shouldUpload && !shouldRemove) {
    return {};
  }

  const formData = new FormData();
  formData.append('placement', placement);

  if (file) {
    formData.append('file', file);
  }

  if (thumbnailFile) {
    formData.append('thumbnail', thumbnailFile);
  }

  if (removeMediaPath) {
    formData.append('removeMediaPath', removeMediaPath);
  }

  if (removeThumbnailPath) {
    formData.append('removeThumbnailPath', removeThumbnailPath);
  }

  const token = await getAuthToken();
  const response = await fetch(`${apiBaseUrl}/media/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Errore durante il caricamento dei file media.');
  }

  return (await response.json()) as UploadResponse;
};

const deleteMediaFiles = async (paths: Array<string | null | undefined>) => {
  const payload = paths.filter((value): value is string => typeof value === 'string' && value.length > 0);
  if (payload.length === 0) {
    return;
  }

  const token = await getAuthToken();
  const response = await fetch(`${apiBaseUrl}/media`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ paths: payload }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Errore durante l\'eliminazione dei file media.');
  }
};

const toMediaAsset = (snapshot: QueryDocumentSnapshot<DocumentData>): MediaAsset => {
  const data = snapshot.data() as Record<string, unknown>;
  const createdAtRaw = data.createdAt as Timestamp | Date | undefined;
  const updatedAtRaw = data.updatedAt as Timestamp | Date | undefined;
  return {
    id: snapshot.id,
    title: (data.title as string) ?? '',
    placement: (data.placement as string) ?? '',
    type: (data.type as MediaAsset['type']) ?? 'image',
    description: data.description as string | undefined,
    mediaUrl: (data.mediaUrl as string) ?? '',
    thumbnailUrl: data.thumbnailUrl as string | undefined,
    order: (data.order as number | null | undefined) ?? null,
    storagePath: data.storagePath as string | null | undefined,
    thumbnailPath: data.thumbnailPath as string | null | undefined,
    createdAt:
      createdAtRaw instanceof Timestamp
        ? createdAtRaw.toDate()
        : createdAtRaw instanceof Date
          ? createdAtRaw
          : undefined,
    updatedAt:
      updatedAtRaw instanceof Timestamp
        ? updatedAtRaw.toDate()
        : updatedAtRaw instanceof Date
          ? updatedAtRaw
          : undefined,
  };
};

const buildFirestorePayload = (payload: Record<string, unknown>): DocumentData => {
  const data: DocumentData = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) {
      data[key] = value;
    }
  });
  return data;
};

export const subscribeToMediaAssets = (
  callback: (assets: MediaAsset[]) => void,
  onError?: (error: Error) => void,
) =>
  onSnapshot(
    query(mediaCollection, orderBy('order', 'asc'), orderBy('createdAt', 'desc')),
    (querySnapshot) => {
      const assets = querySnapshot.docs.map((docSnapshot) => toMediaAsset(docSnapshot));
      callback(assets);
    },
    (error) => {
      onError?.(error as Error);
    },
  );

export const createMediaAsset = async (payload: MediaAssetPayload) => {
  const { file, thumbnailFile, ...rest } = payload;
  const uploadResult = await uploadMediaFiles({
    placement: rest.placement,
    file,
    thumbnailFile,
  });

  await addDoc(
    mediaCollection,
    buildFirestorePayload({
      ...rest,
      mediaUrl: uploadResult.mediaUrl ?? rest.mediaUrl ?? '',
      storagePath: uploadResult.mediaPath ?? null,
      thumbnailUrl: uploadResult.thumbnailUrl ?? rest.thumbnailUrl ?? null,
      thumbnailPath: uploadResult.thumbnailPath ?? null,
      order: rest.order ?? null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }),
  );
};

export const updateMediaAsset = async (
  id: string,
  payload: MediaAssetPayload,
  current: MediaAsset,
) => {
  const assetRef = doc(mediaCollection, id);
  const { file, thumbnailFile, ...rest } = payload;

  const uploadResult = await uploadMediaFiles({
    placement: rest.placement,
    file,
    thumbnailFile,
    removeMediaPath: file ? current.storagePath ?? undefined : undefined,
    removeThumbnailPath: thumbnailFile ? current.thumbnailPath ?? undefined : undefined,
  });

  let mediaUrl = rest.mediaUrl ?? current.mediaUrl;
  let storagePath = current.storagePath ?? null;
  if (uploadResult.mediaUrl) {
    mediaUrl = uploadResult.mediaUrl;
    storagePath = uploadResult.mediaPath ?? storagePath;
  }

  let thumbnailUrl = rest.thumbnailUrl ?? current.thumbnailUrl;
  let thumbnailPath = current.thumbnailPath ?? null;
  if (uploadResult.thumbnailUrl) {
    thumbnailUrl = uploadResult.thumbnailUrl;
    thumbnailPath = uploadResult.thumbnailPath ?? thumbnailPath;
  }

  await updateDoc(
    assetRef,
    buildFirestorePayload({
      ...rest,
      mediaUrl,
      storagePath,
      thumbnailUrl: thumbnailUrl ?? null,
      thumbnailPath,
      order: rest.order ?? null,
      updatedAt: serverTimestamp(),
    }),
  );
};

export const deleteMediaAsset = async (asset: MediaAsset) => {
  const assetRef = doc(mediaCollection, asset.id);
  await deleteDoc(assetRef);

  try {
    await deleteMediaFiles([asset.storagePath, asset.thumbnailPath]);
  } catch (error) {
    console.warn('Unable to delete media files from server', error);
  }
};
